import React, { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Plus, DollarSign, Calendar, Building, Zap, Shield, Truck, Megaphone,
  Monitor, Briefcase, TrendingDown, Package, Book, Plane, MoreHorizontal,
  Edit, Trash2, Receipt, Search, Filter, Download, Upload, RefreshCw,
  Users, ChevronDown, ChevronUp
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import { usePermissions } from "../contexts/PermissionsContext";

const COLORS = ['#3B82F6', '#F59E0B', '#8B5CF6', '#10B981', '#EF4444', '#6366F1', '#EC4899', '#14B8A6', '#F97316', '#84CC16', '#06B6D4', '#A855F7', '#64748B'];

const CATEGORY_ICONS = {
  salaries: Users,
  rent: Building,
  utilities: Zap,
  insurance: Shield,
  vehicle: Truck,
  marketing: Megaphone,
  software: Monitor,
  professional: Briefcase,
  depreciation: TrendingDown,
  office_supplies: Package,
  training: Book,
  travel: Plane,
  misc: MoreHorizontal,
};

const CATEGORY_LABELS = {
  salaries: "Salaries & Wages",
  rent: "Office/Yard Rent",
  utilities: "Utilities",
  insurance: "Insurance",
  vehicle: "Vehicle Expenses",
  marketing: "Marketing",
  software: "Software & Tech",
  professional: "Professional Services",
  depreciation: "Depreciation",
  office_supplies: "Office Supplies",
  training: "Training",
  travel: "Travel",
  misc: "Miscellaneous",
};

const ExpensesPage = () => {
  const { can } = usePermissions();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showChart, setShowChart] = useState(true);

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category: "",
    description: "",
    amount: "",
    vendor: "",
    payment_method: "",
    recurring: false,
    recurring_frequency: "",
    notes: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("year", selectedYear);
      if (selectedMonth && selectedMonth !== "all") params.append("month", selectedMonth);
      if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);

      const [expensesRes, summaryRes, categoriesRes] = await Promise.all([
        API.get(`/expenses?${params.toString()}`),
        API.get(`/expenses/summary?year=${selectedYear}`),
        API.get("/expenses/categories"),
      ]);

      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.category || !form.description || !form.amount || !form.date) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
      };

      if (editExpense) {
        await API.put(`/expenses/${editExpense.expense_id}`, payload);
        toast.success("Expense updated successfully");
      } else {
        await API.post("/expenses", payload);
        toast.success("Expense added successfully");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(editExpense ? "Failed to update expense" : "Failed to add expense");
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return;
    
    try {
      await API.delete(`/expenses/${expenseId}`);
      toast.success("Expense deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  const handleEdit = (expense) => {
    setEditExpense(expense);
    setForm({
      date: expense.date || "",
      category: expense.category || "",
      description: expense.description || "",
      amount: expense.amount?.toString() || "",
      vendor: expense.vendor || "",
      payment_method: expense.payment_method || "",
      recurring: expense.recurring || false,
      recurring_frequency: expense.recurring_frequency || "",
      notes: expense.notes || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditExpense(null);
    setForm({
      date: new Date().toISOString().split('T')[0],
      category: "",
      description: "",
      amount: "",
      vendor: "",
      payment_method: "",
      recurring: false,
      recurring_frequency: "",
      notes: "",
    });
  };

  const filteredExpenses = expenses.filter(exp => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        exp.description?.toLowerCase().includes(search) ||
        exp.vendor?.toLowerCase().includes(search) ||
        exp.category?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const fmt = (num) => new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0 
  }).format(num || 0);

  // Prepare chart data
  const chartDataByCategory = summary?.by_category 
    ? Object.entries(summary.by_category).map(([cat, data]) => ({
        name: CATEGORY_LABELS[cat] || cat,
        value: data.total,
      }))
    : [];

  const chartDataByMonth = summary?.by_month
    ? Object.entries(summary.by_month)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, total]) => ({
          month: month.slice(5), // MM format
          amount: total,
        }))
    : [];

  const months = [
    { value: "all", label: "All Months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="expenses-page" className="space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Operating Expenses</h1>
          <p className="text-slate-400 font-ibm mt-1">Track overhead costs for accurate P&L reporting</p>
        </div>
        <div className="flex items-center gap-3">
          {can('create_expense') && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-expense-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                  <Plus size={18} className="mr-2" />
                  Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg bg-slate-900 border-slate-800 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-chivo text-white">
                    {editExpense ? "Edit Expense" : "Add New Expense"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-400">Date *</label>
                      <Input
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                        data-testid="expense-date-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-400">Amount *</label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                        data-testid="expense-amount-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-400">Category *</label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="expense-category-select" className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-400">Description *</label>
                    <Input
                      placeholder="e.g., Monthly office rent"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      required
                      data-testid="expense-description-input"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-400">Vendor/Payee</label>
                    <Input
                      placeholder="e.g., Landlord Name"
                      value={form.vendor}
                      onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-400">Payment Method</label>
                    <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="check">Check</SelectItem>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="ach">ACH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-white">Recurring Expense</p>
                      <p className="text-sm text-slate-500">This expense repeats regularly</p>
                    </div>
                    <Switch
                      checked={form.recurring}
                      onCheckedChange={(checked) => setForm({ ...form, recurring: checked })}
                    />
                  </div>

                  {form.recurring && (
                    <div>
                      <label className="text-sm font-medium text-slate-400">Frequency</label>
                      <Select value={form.recurring_frequency} onValueChange={(v) => setForm({ ...form, recurring_frequency: v })}>
                        <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-slate-400">Notes</label>
                    <Textarea
                      placeholder="Additional notes..."
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      rows={2}
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="save-expense-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                      {editExpense ? "Update" : "Add"} Expense
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
            <Calendar size={16} className="mr-2 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {[2024, 2025, 2026].map(year => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
            <SelectValue placeholder="All Months" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {months.map(m => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Search expenses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>

        <Button variant="outline" onClick={() => setShowChart(!showChart)} className="border-slate-700">
          {showChart ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          Charts
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-900/50 to-slate-900 border-blue-700/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-blue-400 mb-2">
              <DollarSign size={18} />
              <span className="text-sm font-medium">Total Expenses</span>
            </div>
            <p className="text-3xl font-mono font-bold text-white">{fmt(summary?.total || 0)}</p>
            <p className="text-sm text-slate-500 mt-1">{summary?.count || 0} entries in {selectedYear}</p>
          </CardContent>
        </Card>
        
        {Object.entries(summary?.by_category || {}).slice(0, 3).map(([cat, data]) => {
          const Icon = CATEGORY_ICONS[cat] || MoreHorizontal;
          return (
            <Card key={cat} className="bg-slate-800 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-400 mb-2">
                  <Icon size={18} />
                  <span className="text-sm font-medium">{CATEGORY_LABELS[cat] || cat}</span>
                </div>
                <p className="text-2xl font-mono font-bold text-white">{fmt(data.total)}</p>
                <p className="text-sm text-slate-500 mt-1">{data.count} entries</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      {showChart && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Trend */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Monthly Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              {chartDataByMonth.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartDataByMonth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month" stroke="#94a3b8" />
                    <YAxis stroke="#94a3b8" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                      formatter={(value) => fmt(value)}
                    />
                    <Bar dataKey="amount" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-500">
                  No expense data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* By Category Pie */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Expenses by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {chartDataByCategory.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={chartDataByCategory}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name.split(' ')[0]} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartDataByCategory.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                      formatter={(value) => fmt(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-500">
                  No expense data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expenses Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Expense Details</CardTitle>
            <Badge variant="outline" className="text-slate-400">
              {filteredExpenses.length} expenses
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <Receipt size={48} className="mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No expenses found</h3>
              <p className="text-slate-500 mb-4">Add your first expense to start tracking</p>
              {can('create_expense') && (
                <Button onClick={() => setDialogOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900">
                  <Plus size={16} className="mr-2" />
                  Add Expense
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Category</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Description</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Vendor</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Amount</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Recurring</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filteredExpenses.map((expense) => {
                    const Icon = CATEGORY_ICONS[expense.category] || MoreHorizontal;
                    return (
                      <tr key={expense.expense_id} className="hover:bg-slate-800/50">
                        <td className="px-4 py-3 text-slate-300">{expense.date}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Icon size={16} className="text-slate-500" />
                            <span className="text-white">{CATEGORY_LABELS[expense.category] || expense.category}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-300">{expense.description}</td>
                        <td className="px-4 py-3 text-slate-400">{expense.vendor || "-"}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-white">{fmt(expense.amount)}</td>
                        <td className="px-4 py-3 text-center">
                          {expense.recurring ? (
                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                              {expense.recurring_frequency || "Yes"}
                            </Badge>
                          ) : (
                            <span className="text-slate-600">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {can('edit_expense') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(expense)}
                                className="text-slate-400 hover:text-white"
                              >
                                <Edit size={14} />
                              </Button>
                            )}
                            {can('delete_expense') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(expense.expense_id)}
                                className="text-slate-400 hover:text-red-400"
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpensesPage;
